// §4.2 — THE CUSTOMER 360's DOCUMENTS TAB (spec 09 §4.2's E2-2a2 annotation,
// D-323 ruling 14). The tab §4.2 has named since P3-A lands, because its data
// has existed since P3-C and P3-D. Two sections, each with its own skeleton,
// empty state and error with request id, stacked on a phone.
//
// DOCUMENTS. The customer's quotes and orders as one timeline, newest first,
// twenty a page of each with Show more (`mergeTimeline`,
// hooks/document-queries.ts). Each row carries its kind, its number (a link
// only into a screen the reader opens, D-088), its status, its total for a
// value reader (§7's money is omission), its date in the plant's clock, and
// its PDF door. A reader without `sales.read_value` is offered no PDF,
// because every priced door refuses that reader (§7 narrowing 7). The quote
// list does not serve `pdf_available`, so a quote row offers its PDF once a
// revision was sent (`sent_revision_no`), the list's one fact about a send. An
// order row offers the proforma on every status the door serves
// (`canOpenProforma`). An order row also opens the order's Shipments panel
// with its delivery notes, read from the order's own detail when the reader
// asks, because `GET /v1/shipments` takes no customer filter. The row shows
// no shipment count: `OrderListItem` serves none, and a count taken in the
// list query would count only the shipments the reader's facility fence
// shows, so two readers of one order would read two numbers (§4.2).
//
// ATTACHED FILES. The customer's own attachments (`AttachmentsPanel`), with
// Upload and Detach for a writer of the customer record (narrowing 22's
// lineage, `canAttachTo`). An archived customer takes no new file and says
// so in place of Upload; a merged customer is the tombstone, whose files
// moved to the winner (§3.13), so it offers no write at all.

import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../../components/data-table";
import { ErrorState, Skeleton } from "../../../../../components/states";
import { StatusChip } from "../../../../../components/status-chip";
import { formatTimestamp } from "../../../../../lib/datetime";
import { requestIdOf } from "../../../../../lib/request";
import { useTenantTimezone } from "../../../../../lib/role-codes";
import { orderStatusVariant } from "../../lib/chips";
import {
  NOTE_STATUSES,
  canOpenProforma,
  canOpenQuotePdf,
  deliveryNotePath,
  proformaPath,
  quotePdfPath,
} from "../../lib/documents";
import { formatMinor } from "../../lib/money";
import { quoteStatusVariant } from "../../lib/quotes";
import { useReadsHeldOffline } from "../../lib/reads";
import { canAttachTo, useSalesPermissions } from "../../hooks/permissions";
import { useOrder } from "../../hooks/quote-queries";
import {
  mergeTimeline,
  useCustomerOrderDocuments,
  useCustomerQuoteDocuments,
  type DocumentRow,
} from "../../hooks/document-queries";
import type { Customer360 } from "../../hooks/sales-queries";
import { AttachmentsPanel } from "./attachments-panel";
import "./attachments.css";
import { bookClientPager } from "../../hooks/book-pages";

export function DocumentsTab({ data }: { readonly data: Customer360 }) {
  const { t } = useTranslation();
  const perms = useSalesPermissions();
  const customer = data.customer;
  const merged = (customer.merged_into_customer_id ?? null) !== null;
  const writer = canAttachTo(perms, "customer") && !merged;
  return (
    <div className="sales-360-documents" data-testid="sales-360-documents">
      <DocumentsSection customerId={customer.id} />
      <AttachmentsPanel
        subject={{ kind: "customer", id: customer.id }}
        canWrite={writer}
        writeClosedNote={
          customer.status === "archived" ? t("sales.attachments.customerArchived") : undefined
        }
        mergedWinnerName={customer.merged_into_display_name ?? undefined}
        wide
      />
    </div>
  );
}

function DocumentsSection({ customerId }: { readonly customerId: string }) {
  const { t, i18n } = useTranslation();
  const perms = useSalesPermissions();
  const timeZone = useTenantTimezone();
  const offline = useReadsHeldOffline();
  const has = (code: string) => perms.has(code);
  const quotes = useCustomerQuoteDocuments(customerId);
  const orders = useCustomerOrderDocuments(customerId);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());

  const timeline = mergeTimeline(
    {
      items: quotes.data?.pages.flatMap((page) => page.items) ?? [],
      hasMore: quotes.hasNextPage,
    },
    {
      items: orders.data?.pages.flatMap((page) => page.items) ?? [],
      hasMore: orders.hasNextPage,
    },
  );
  const moreAvailable = timeline.fetch.quotes || timeline.fetch.orders;
  const fetchingMore = quotes.isFetchingNextPage || orders.isFetchingNextPage;
  const moreFailed = quotes.isFetchNextPageError || orders.isFetchNextPageError;
  // The first read that failed with nothing to show: either list's failure
  // alone turns the section into its error card, because half a timeline
  // would read as complete.
  const firstError =
    (quotes.data === undefined ? quotes.error : null) ??
    (orders.data === undefined ? orders.error : null);

  const toggle = (orderId: string) => {
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const columns: DataTableColumn<DocumentRow>[] = [
    {
      key: "number",
      header: t("sales.documentsTab.col.number"),
      id: true,
      render: (row) =>
        row.kind === "quote" ? (
          perms.canOpenQuotes ? (
            <Link
              to="/sales/quotes/$quoteId"
              params={{ quoteId: row.doc.id }}
              className="ds-lnk"
              data-testid="sales-document-link"
            >
              {row.doc.quote_no}
            </Link>
          ) : (
            <span data-testid="sales-document-no">{row.doc.quote_no}</span>
          )
        ) : perms.canOpenOrders ? (
          <Link
            to="/sales/orders/$orderId"
            params={{ orderId: row.doc.id }}
            className="ds-lnk"
            data-testid="sales-document-link"
          >
            {row.doc.order_no}
          </Link>
        ) : (
          <span data-testid="sales-document-no">{row.doc.order_no}</span>
        ),
    },
    {
      key: "kind",
      header: t("sales.documentsTab.col.kind"),
      render: (row) => (
        <span data-testid="sales-document-kind">{t(`sales.documentsTab.kind.${row.kind}`)}</span>
      ),
    },
    {
      key: "status",
      header: t("sales.documentsTab.col.status"),
      render: (row) =>
        row.kind === "quote" ? (
          <StatusChip variant={quoteStatusVariant(row.doc.status)} dot={false}>
            {t(`sales.quoteStatus.${row.doc.status}`)}
          </StatusChip>
        ) : (
          <StatusChip variant={orderStatusVariant(row.doc.status)} dot={false}>
            {t(`sales.orders.status.${row.doc.status}`)}
          </StatusChip>
        ),
    },
    {
      key: "total",
      header: t("sales.documentsTab.col.total"),
      numeric: true,
      // §7 omission: no `sales.read_value`, no figure and no column.
      presence: (rows) => rows.some((row) => row.doc.total_minor !== undefined),
      render: (row) =>
        row.doc.total_minor !== undefined ? (
          <span className="num" data-testid="sales-document-total">
            {formatMinor(row.doc.total_minor, row.doc.currency, i18n.language)}
          </span>
        ) : null,
    },
    {
      key: "at",
      header: t("sales.documentsTab.col.created"),
      age: true,
      render: (row) => (
        <span className="num">{formatTimestamp(row.at, i18n.language, timeZone)}</span>
      ),
    },
    {
      key: "actions",
      header: t("sales.documentsTab.col.actions"),
      actions: true,
      render: (row) => (
        <span className="sales-attach-acts">
          {row.kind === "quote" &&
          canOpenQuotePdf({ pdf_available: row.doc.sent_revision_no !== null }, has) ? (
            <a
              className="ds-lnk"
              href={quotePdfPath(row.doc.id)}
              target="_blank"
              rel="noreferrer"
              data-testid="sales-document-pdf"
            >
              {t("sales.documentsTab.quotePdf")}
            </a>
          ) : null}
          {row.kind === "order" && canOpenProforma(row.doc.status, has) ? (
            <a
              className="ds-lnk"
              href={proformaPath(row.doc.id)}
              target="_blank"
              rel="noreferrer"
              data-testid="sales-document-pdf"
            >
              {t("sales.documentsTab.proforma")}
            </a>
          ) : null}
          {row.kind === "order" ? (
            <Button
              size="sm"
              data-testid="sales-document-shipments"
              aria-expanded={open.has(row.doc.id)}
              onClick={() => {
                toggle(row.doc.id);
              }}
            >
              {open.has(row.doc.id)
                ? t("sales.documentsTab.hideShipments")
                : t("sales.documentsTab.shipments")}
            </Button>
          ) : null}
        </span>
      ),
    },
  ];

  const body = (() => {
    if (quotes.data === undefined || orders.data === undefined) {
      if (firstError !== null) {
        return (
          <div data-testid="sales-documents-error">
            <ErrorState
              title={t("sales.documentsTab.error")}
              retryLabel={t("sales.common.retry")}
              requestIdLabel={t("sales.common.requestId")}
              requestId={requestIdOf(firstError)}
              onRetry={() => {
                if (quotes.error !== null) void quotes.refetch();
                if (orders.error !== null) void orders.refetch();
              }}
            />
          </div>
        );
      }
      if (quotes.fetchStatus === "paused" || orders.fetchStatus === "paused") {
        return (
          <p className="sales-hint" role="status" data-testid="sales-documents-offline">
            {t("sales.documentsTab.readOffline")}
          </p>
        );
      }
      return (
        <div className="sales-attach-skeleton" data-testid="sales-documents-loading">
          <p className="visually-hidden" role="status">
            {t("sales.documentsTab.loading")}
          </p>
          {[0, 1, 2].map((row) => (
            <div key={row} className="sales-attach-skeleton-row" aria-hidden="true">
              <Skeleton height="1rem" />
              <Skeleton width="40%" height="0.8rem" />
            </div>
          ))}
        </div>
      );
    }
    return (
      <>
        <DataTable
          data-testid="sales-documents-table"
          columns={columns}
          rows={timeline.rows}
          // 50 rows a page (D-560). Show more reads further back and opens
          // the last page, where the older documents land.
          clientPager={bookClientPager(t, i18n.language, { appendable: true })}
          rowKey={(row) => `${row.kind}:${row.doc.id}`}
          rowTestId="sales-document-row"
          rowProps={(row) => ({
            "data-kind": row.kind,
            "data-document-id": row.doc.id,
            "data-status": row.doc.status,
          })}
          caption={t("sales.documentsTab.title")}
          fit
          expansion={(row) =>
            row.kind === "order" && open.has(row.doc.id) ? (
              <OrderShipments orderId={row.doc.id} />
            ) : null
          }
          expansionLabel={() => t("sales.documentsTab.shipmentsTitle")}
          empty={
            <p className="sales-hint" data-testid="sales-documents-empty">
              {t("sales.documentsTab.empty")}
            </p>
          }
        />
        {moreAvailable ? (
          <div className="sales-toolbar">
            <Button
              size="sm"
              data-testid="sales-documents-more"
              disabled={offline}
              busy={fetchingMore}
              busyLabel={t("sales.common.loading")}
              onClick={() => {
                if (timeline.fetch.quotes) void quotes.fetchNextPage();
                if (timeline.fetch.orders) void orders.fetchNextPage();
              }}
            >
              {t("sales.common.loadMore")}
            </Button>
          </div>
        ) : null}
        {moreFailed ? (
          <p className="sales-error-note" role="alert" data-testid="sales-documents-more-failed">
            {t("sales.documentsTab.moreFailed")}
          </p>
        ) : null}
      </>
    );
  })();

  return (
    <section
      className="ds-card sales-360-card sales-360-wide sales-documents"
      data-testid="sales-360-documents-list"
    >
      <h3>{t("sales.documentsTab.title")}</h3>
      {body}
    </section>
  );
}

/**
 * The order's Shipments panel, opened from its row: the shipments the
 * reader's order door serves with their delivery notes, read from the order's
 * own detail (`GET /v1/orders/{id}`) only once the row is opened. A note on
 * file is a link (the door is `sales.read`); a packed-or-later shipment with
 * none says so; drawing a missing note is the order detail's verb, where the
 * facility fence that guards it is read.
 */
function OrderShipments({ orderId }: { readonly orderId: string }) {
  const { t, i18n } = useTranslation();
  const timeZone = useTenantTimezone();
  const detail = useOrder(orderId);
  if (detail.data === undefined) {
    if (detail.error !== null) {
      return (
        <p className="sales-error-note" role="alert" data-testid="sales-document-shipments-error">
          {t("sales.documentsTab.shipmentsError")}
        </p>
      );
    }
    return (
      <p className="sales-hint" role="status" data-testid="sales-document-shipments-loading">
        {detail.fetchStatus === "paused" ? t("sales.offline.body") : t("sales.common.loading")}
      </p>
    );
  }
  const shipments = detail.data.shipments;
  return (
    <div className="sales-document-shipments" data-testid="sales-document-shipments-panel">
      {shipments.length === 0 ? (
        <p className="sales-hint" data-testid="sales-document-shipments-empty">
          {t("sales.documentsTab.shipmentsEmpty")}
        </p>
      ) : (
        <ul className="sales-document-shipment-list">
          {shipments.map((shipment) => (
            <li
              key={shipment.id}
              data-testid="sales-document-shipment"
              data-shipment-id={shipment.id}
              data-status={shipment.status}
            >
              <strong>{shipment.shipment_no}</strong>
              <StatusChip
                variant={
                  shipment.status === "delivered"
                    ? "ok"
                    : shipment.status === "shipped"
                      ? "accent"
                      : "neutral"
                }
                dot={false}
              >
                {t(`sales.shipments.status.${shipment.status}`)}
              </StatusChip>
              {shipment.shipped_at !== null ? (
                <span className="num sales-hint">
                  {formatTimestamp(shipment.shipped_at, i18n.language, timeZone)}
                </span>
              ) : null}
              {shipment.delivery_note_file_id !== null ? (
                <a
                  className="ds-lnk"
                  href={deliveryNotePath(shipment.id)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="sales-document-delivery-note"
                >
                  {t("sales.shipments.deliveryNote")}
                </a>
              ) : NOTE_STATUSES.has(shipment.status) ? (
                <span className="sales-hint" data-testid="sales-document-delivery-note-missing">
                  {t("sales.shipments.deliveryNoteMissing")}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
